import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import { useSidebarLayout } from "../../../../contexts/SidebarLayoutContext";
import { customerService } from "../../../../services/api/customerService";
import { departmentService } from "../../../../services/api/departmentService";
import { employeeService } from "../../../../services/api/employeeService";
import { firebaseService } from "../../../../services/api/firebaseService";
import Modal from "../../components/Modal";
import TabAccountInfo from "./AddNewTabs/TabAccountInfo";
import TabGeneralInfo from "./AddNewTabs/TabGeneralInfo";
import useDebouncedSearch from "../../../../hooks/useDebouncedSearch";

const emptyForm = {
  avatar: null,
  companyName: "",
  fullName: "",
  email: "",
  phone: "",
  website: "",
  zalo: "",
  MST: "",
  city: "",
  district: "",
  detail: "",
  staffCode: "",
  username: "",
  password: "",
  role: "",
  status: "",
  avatarUrl: "",
  responsibleDepartment: null,
  responsibleStaff: null,
  supervisors: [],
};

const emailRe = /\S+@\S+\.\S+/;
const phoneRe = /^\+?\d{9,15}$/;

const ITEMS_PER_PAGE = 7;

const AddCustomerPopup = ({
  isVisible,
  onClose,
  onSubmit,
  data = null,
  mode = "create",
}) => {
  const { token } = useContext(AuthContext);
  const { hasPermission } = useSidebarLayout();
  const isEdit = mode === "edit";

  const canEditDept = hasPermission("chinh_sua_phong_ban_phu_trach_khach_hang");
  const canEditStaff = hasPermission("chinh_sua_nhan_vien_phu_trach_khach_hang");

  const [deptSearch, setDeptSearch] = useState("");
  const [staffSearch, setStaffSearch] = useState("");
  const isFirstDeptLoad = useRef(true);

  const [deptPage, setDeptPage] = useState(1);
  const [deptHasMore, setDeptHasMore] = useState(true);
  const [deptLoadingMore, setDeptLoadingMore] = useState(false);
  const [deptInitialized, setDeptInitialized] = useState(false);

  const [staffPage, setStaffPage] = useState(1);
  const [staffHasMore, setStaffHasMore] = useState(true);
  const [staffLoadingMore, setStaffLoadingMore] = useState(false);
  const [staffInitialized, setStaffInitialized] = useState(false);

  const [supervisorSearch, setSupervisorSearch] = useState("");
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [supervisorPage, setSupervisorPage] = useState(1);
  const [supervisorHasMore, setSupervisorHasMore] = useState(true);
  const [supervisorLoadingMore, setSupervisorLoadingMore] = useState(false);
  const [supervisorInitialized, setSupervisorInitialized] = useState(false);

  const [cancelModal, setCancelModal] = useState(false);

  const initialForm = useMemo(() => {
    if (!isEdit || !data) return emptyForm;

    let responsibleStaffValue = null;
    if (data.assignedStaff) {
      responsibleStaffValue = {
        value: data.assignedStaff._id,
        label: data.assignedStaff.fullName,
        accountId: data.assignedStaff._id,
        employeeId: data.assignedStaff._id,
      };
    }

    let supervisorsValue = [];
    if (data.supervisors && Array.isArray(data.supervisors)) {
      supervisorsValue = data.supervisors.map((s) => {
        if (typeof s === "object" && s !== null) {
          return {
            value: s._id || s.id || s.accountId,
            label: s.fullName || s.name || "Không có tên",
          };
        }
        return { value: s, label: s };
      });
    }

    return {
      ...emptyForm,
      ...data,
      avatarUrl: data.avatarUrl || "",
      responsibleDepartment: data.responsibleDepartmentId
        ? {
          value: data.responsibleDepartmentId._id,
          label: data.responsibleDepartmentId.name,
        }
        : null,
      responsibleStaff: responsibleStaffValue,
      supervisors: supervisorsValue,
      role:
        data.role?.name === "Manager"
          ? { value: "Manager", label: "Quản lý" }
          : data.role?.name
            ? { value: data.role.name, label: data.role.name }
            : "",
      status:
        data.status === "Đang hoạt động"
          ? { value: "Đang hoạt động", label: "Đang hoạt động" }
          : { value: "Không hoạt động", label: "Không hoạt động" },
      city: data?.address?.city || data.city || "",
      district: data?.address?.district || data.district || "",
      detail: data?.address?.detail || data.detail || "",
    };
  }, [isEdit, data]);

  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);

  const prevDeptIdRef = useRef(null);

  useEffect(() => {
    if (isEdit && data?.responsibleDepartmentId) {
      setDepartmentOptions([
        {
          value: data.responsibleDepartmentId._id,
          label: data.responsibleDepartmentId.name,
        },
      ]);
    } else {
      setDepartmentOptions([]);
    }
  }, [isEdit, data]);

  const fetchDepartments = async (page = 1, searchValue = "") => {
    try {
      if (page === 1) setDepartmentOptions([]);
      setDeptLoadingMore(true);

      const res = await departmentService.get_list_department_short(token, {
        page,
        limit: ITEMS_PER_PAGE,
        search_value: searchValue || undefined,
      });

      const list = res?.data || [];

      setDepartmentOptions((prev) => [
        ...prev,
        ...list.map((d) => ({
          value: d._id,
          label: d.name,
        })),
      ]);

      setDeptHasMore(page < res.pagination.totalPages);
      setDeptPage(page);
      setDeptInitialized(true);
    } catch (error) {
      console.error("Error fetching departments:", error);
      toast.error("Không thể tải danh sách phòng ban");
    } finally {
      setDeptLoadingMore(false);
    }
  };

  const fetchStaff = useCallback(
    async (page = 1, searchValue = "") => {
      try {
        if (page === 1) setStaffOptions([]);
        setStaffLoadingMore(true);

        const deptId = formData.responsibleDepartment?.value;

        if (deptId) {
          // TH1: Có phòng ban → lấy nhân viên theo phòng ban
          const res = await employeeService.get_list_employees_of_department(
            token,
            deptId,
            {
              page,
              limit: ITEMS_PER_PAGE,
              ...(searchValue ? { search_value: searchValue } : {}),
            },
          );

          if (
            !res?.data ||
            (Array.isArray(res.data) && res.data.length === 0)
          ) {
            setStaffOptions([]);
            if (isEdit && formData.responsibleStaff) {
              setFormData((prev) => ({ ...prev, responsibleStaff: null }));
              toast.info(
                "Không tìm thấy nhân viên trong phòng ban này. Vui lòng chọn lại.",
              );
            }
            setStaffHasMore(false);
            setStaffInitialized(true);
            setStaffLoadingMore(false);
            return;
          }

          const mappedStaff = (res.data || []).map((emp) => ({
            value: emp.accountId,
            label: emp.fullName || "Không có tên",
            accountId: emp.accountId,
            employeeId: emp.id,
          }));

          if (page === 1) {
            setStaffOptions(mappedStaff);
          } else {
            setStaffOptions((prev) => [...prev, ...mappedStaff]);
          }

          setStaffHasMore(page < (res.totalPages || 1));
          setStaffPage(page);
        } else {
          // TH2: Không có phòng ban → lấy toàn bộ nhân viên short
          const res = await employeeService.get_list_staff_short(token, {
            page,
            limit: ITEMS_PER_PAGE,
            ...(searchValue ? { search_value: searchValue } : {}),
          });

          const mappedStaff = (res?.data || []).map((emp) => ({
            value: emp.accountId,
            label: emp.fullName || "Không có tên",
            accountId: emp.accountId,
            employeeId: emp._id,
          }));

          setStaffOptions((prev) => [...prev, ...mappedStaff]);
          setStaffHasMore(page < (res?.totalPages || 1));
          setStaffPage(page);
        }

        setStaffInitialized(true);
      } catch (error) {
        console.error("Error fetching staff:", error);
        toast.error("Không thể tải danh sách nhân viên");
        setStaffOptions([]);
        if (isEdit && formData.responsibleStaff) {
          setFormData((prev) => ({ ...prev, responsibleStaff: null }));
        }
        setStaffInitialized(true);
      } finally {
        setStaffLoadingMore(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, formData.responsibleDepartment?.value, isEdit, data],
  );

  const fetchSupervisors = useCallback(
    async (page = 1, searchValue = "") => {
      try {
        if (page === 1) setSupervisorOptions([]);
        setSupervisorLoadingMore(true);

        const res = await employeeService.get_list_staff_short(token, {
          page,
          limit: ITEMS_PER_PAGE,
          ...(searchValue ? { search_value: searchValue } : {}),
        });

        const mappedStaff = (res?.data || []).map((emp) => ({
          value: emp.accountId,
          label: emp.fullName || "Không có tên",
          accountId: emp.accountId,
          employeeId: emp._id,
        }));

        setSupervisorOptions((prev) => [...prev, ...mappedStaff]);
        setSupervisorHasMore(page < (res?.totalPages || 1));
        setSupervisorPage(page);
        setSupervisorInitialized(true);
      } catch (error) {
        console.error("Error fetching supervisors:", error);
        toast.error("Không thể tải danh sách giám sát viên");
      } finally {
        setSupervisorLoadingMore(false);
      }
    },
    [token],
  );

  const loadMoreDepartments = () => {
    if (!deptHasMore || deptLoadingMore) return;
    fetchDepartments(deptPage + 1, deptSearch);
  };

  const loadMoreStaff = () => {
    if (!staffHasMore || staffLoadingMore) return;
    fetchStaff(staffPage + 1, staffSearch);
  };

  const loadMoreSupervisors = () => {
    if (!supervisorHasMore || supervisorLoadingMore) return;
    fetchSupervisors(supervisorPage + 1, supervisorSearch);
  };

  const { debouncedSearch: searchDepartments, searchImmediately: submitDepartmentSearch } =
    useDebouncedSearch((value) => {
      setDeptPage(1);
      fetchDepartments(1, value);
    }, 300);
  const { debouncedSearch: searchStaff, searchImmediately: submitStaffSearch } =
    useDebouncedSearch((value) => {
      setStaffPage(1);
      fetchStaff(1, value);
    }, 300);
  const {
    debouncedSearch: searchSupervisors,
    searchImmediately: submitSupervisorSearch,
  } = useDebouncedSearch((value) => {
    setSupervisorPage(1);
    fetchSupervisors(1, value);
  }, 300);

  const handleDeptSearch = (value) => {
    setDeptSearch(value);
    searchDepartments(value);
  };

  const handleStaffSearch = (value) => {
    setStaffSearch(value);
    searchStaff(value);
  };

  const handleSupervisorSearch = (value) => {
    setSupervisorSearch(value);
    searchSupervisors(value);
  };

  const handleDeptDropdownOpen = () => {
    if (!deptInitialized) fetchDepartments(1, "");
  };

  const handleStaffDropdownOpen = () => {
    if (!staffInitialized) fetchStaff(1, "");
  };

  const handleSupervisorDropdownOpen = () => {
    if (!supervisorInitialized) fetchSupervisors(1, "");
  };

  useEffect(() => {
    if (!isVisible) {
      setDeptSearch("");
      setStaffSearch("");
      setSupervisorSearch("");
      setDeptPage(1);
      setStaffPage(1);
      setSupervisorPage(1);
      setDeptInitialized(false);
      setStaffInitialized(false);
      setSupervisorInitialized(false);
      setDeptHasMore(true);
      setStaffHasMore(true);
      setSupervisorHasMore(true);
      setStaffOptions([]);
      setSupervisorOptions([]);
      setFormData(initialForm);
      setErrors({});
      isFirstDeptLoad.current = true;
      prevDeptIdRef.current = null;
    }
  }, [isVisible, initialForm]);

  useEffect(() => {
    setFormData(initialForm);
    setErrors({});
  }, [initialForm]);

  useEffect(() => {
    const newDeptId = formData.responsibleDepartment?.value ?? null;

    if (isEdit && isFirstDeptLoad.current) {
      isFirstDeptLoad.current = false;
      prevDeptIdRef.current = newDeptId;
      return;
    }

    if (newDeptId === prevDeptIdRef.current) return;
    prevDeptIdRef.current = newDeptId;

    setFormData((prev) => ({ ...prev, responsibleStaff: null }));
    setStaffPage(1);
    setStaffSearch("");
    setStaffOptions([]);
    setStaffHasMore(true);
    setStaffInitialized(false);
  }, [formData.responsibleDepartment?.value, isEdit]);

  useEffect(() => {
    if (isEdit && formData.responsibleDepartment?.value && !staffInitialized) {
      fetchStaff(1, "");
    }
  }, [isEdit, formData.responsibleDepartment?.value, staffInitialized, fetchStaff]);

  const validateFields = () => {
    const newErrors = {};

    if (!formData.companyName?.trim())
      newErrors.companyName = "Vui lòng nhập tên công ty/tổ chức.";
    if (!formData.fullName?.trim())
      newErrors.fullName = "Vui lòng nhập người đại diện.";

    if (!formData.email?.trim()) newErrors.email = "Vui lòng nhập email.";
    else if (!emailRe.test(formData.email))
      newErrors.email = "Email không hợp lệ.";

    if (!formData.phone?.trim())
      newErrors.phone = "Vui lòng nhập số điện thoại.";
    else if (!phoneRe.test(formData.phone))
      newErrors.phone = "Số điện thoại không hợp lệ.";

    if (!formData.username?.trim())
      newErrors.username = "Vui lòng nhập tên đăng nhập.";
    if (!isEdit && !formData.password)
      newErrors.password = "Vui lòng nhập mật khẩu.";
    if (formData.password && formData.password.length < 6)
      newErrors.password = "Mật khẩu phải có ít nhất 6 ký tự.";

    if (!formData.role?.value) newErrors.role = "Vui lòng chọn phân quyền.";
    if (!formData.status?.value) newErrors.status = "Vui lòng chọn trạng thái.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleCancelClick = () => setCancelModal(true);

  const handleCancelConfirm = () => {
    setCancelModal(false);
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!validateFields()) {
      toast.error("Vui lòng kiểm tra lại các trường bị lỗi.");
      return;
    }

    setIsLoading(true);

    try {
      let avatarUrl = formData.avatarUrl || "";

      if (formData.avatar instanceof File) {
        const uploadRes = await firebaseService.upload_file(
          token,
          formData.avatar,
        );

        if (!uploadRes?.url) {
          toast.error("Upload avatar thất bại");
          setIsLoading(false);
          return;
        }

        avatarUrl = uploadRes.url;

        if (
          isEdit &&
          formData.avatarUrl &&
          formData.avatarUrl.startsWith("https://")
        ) {
          try {
            await firebaseService.delete_file(token, formData.avatarUrl);
          } catch (err) {
            console.warn("Failed to delete old avatar:", err);
          }
        }
      }

      const payload = {
        companyName: formData.companyName,
        fullName: formData.fullName,
        website: formData.website || "",
        email: formData.email,
        phone: formData.phone,
        zalo: formData.zalo || "",
        MST: formData.MST || "",
        address: {
          province: formData.province || "",
          city: formData.city || "",
          district: formData.district || "",
          detail: formData.detail || "",
        },
        username: formData.username,
        role: formData.role?.value,
        status: formData.status?.value,
        avatarUrl: avatarUrl,
        responsibleDepartmentId: formData.responsibleDepartment?.value || null,
        assignedStaff: formData.responsibleStaff?.value || null,
        supervisors: formData.supervisors?.map(s => s.value) || [],
      };

      // staffCode chỉ gửi khi edit — backend tự sinh khi tạo mới
      if (isEdit && formData.staffCode) {
        payload.staffCode = formData.staffCode;
      }

      if (formData.password) {
        payload.password = formData.password;
      }

      if (isEdit) {
        const id = data?.infoAccountID || data._id || data.accountId || data.id;
        await customerService.update(id, payload, token);
        toast.success("Khách hàng đã được cập nhật thành công!");
      } else {
        await customerService.create(payload, token);
        toast.success("Khách hàng đã được tạo thành công!");
      }

      onSubmit?.();
      onClose?.();
    } catch (error) {
      toast.dismiss();
      toast.error(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
      console.error("Add/Update customer error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      <Modal
        isOpen={isVisible}
        onClose={onClose}
        title={mode === "edit" ? "Chỉnh sửa khách hàng" : "Thêm khách hàng"}
        maxWidth="60%"
        maxHeight="calc(100vh - 100px)"
        primaryButtonText={isEdit ? "Lưu thay đổi" : "Thêm mới"}
        primaryButtonOnClick={handleSubmit}
        cancelButtonText="Hủy"
        secondaryButtonOnClick={handleCancelClick}
      >
        <div className="px-4 py-6 max-h-[calc(100vh-300px)] overflow-auto space-y-4">
          <TabGeneralInfo
            departmentOptions={departmentOptions}
            staffOptions={staffOptions}
            supervisorOptions={supervisorOptions}
            formData={formData}
            onInputChange={handleInputChange}
            errors={errors}
            isEdit={isEdit}
            deptHasMore={deptHasMore}
            deptLoadingMore={deptLoadingMore}
            loadMoreDepartments={loadMoreDepartments}
            onDeptSearch={handleDeptSearch}
            onDeptSearchSubmit={submitDepartmentSearch}
            onDeptDropdownOpen={handleDeptDropdownOpen}
            staffHasMore={staffHasMore}
            staffLoadingMore={staffLoadingMore}
            loadMoreStaff={loadMoreStaff}
            onStaffSearch={handleStaffSearch}
            onStaffSearchSubmit={submitStaffSearch}
            onStaffDropdownOpen={handleStaffDropdownOpen}
            supervisorHasMore={supervisorHasMore}
            supervisorLoadingMore={supervisorLoadingMore}
            loadMoreSupervisors={loadMoreSupervisors}
            onSupervisorSearch={handleSupervisorSearch}
            onSupervisorSearchSubmit={submitSupervisorSearch}
            onSupervisorDropdownOpen={handleSupervisorDropdownOpen}
            canEditDept={canEditDept}
            canEditStaff={canEditStaff}
          />
          <TabAccountInfo
            formData={formData}
            onInputChange={handleInputChange}
            errors={errors}
            isEdit={isEdit}
          />
        </div>
      </Modal>

      <Modal
        isOpen={cancelModal}
        onClose={() => setCancelModal(false)}
        title="Xác nhận hủy"
        maxWidth="400px"
        primaryButtonText="Đồng ý"
        primaryButtonOnClick={handleCancelConfirm}
        cancelButtonText="Quay lại"
      >
        <div className="px-6 py-4">
          <p className="text-gray-700">
            Bạn có chắc chắn muốn hủy thao tác không?
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Các thay đổi chưa lưu sẽ bị mất.
          </p>
        </div>
      </Modal>
    </>
  );
};

export default AddCustomerPopup;
